/**
 * @module reaction-builder
 * @category Internal
 *
 * Reaction dispatch — what runs inside the drain pipeline once `run_drain_cycle`
 * has fetched events for a leased stream. Two shapes:
 *
 * - per-event `handle`: walks payloads sequentially, running each handler
 *   inside the reaction scope so `action()` threads `reactingTo`
 * - bulk `handle_batch`: hands every event for a static-target projection to
 *   a single batch callback, enabling one-transaction replays
 *
 * Both share `_finalize`, which collapses the retry-vs-block decision and
 * the "report error only when nothing was handled" rule.
 *
 * @internal
 */

import {
  CloseSignal,
  compute_backoff_delay,
  DeferSignal,
  type Handle,
  type HandleBatch,
  type HandleResult,
  resolve_defer_at,
} from "../internal/index.js";

import type { ReactionScope } from "../scoped.js";
import {
  type Actor,
  type BatchHandler,
  type Committed,
  type Lease,
  type Logger,
  NonRetryableError,
  type ReactionOptions,
  type ReactionPayload,
  type Schemas,
} from "../types/index.js";

/**
 * What the dispatcher needs from the orchestrator: a logger for retry and
 * error breadcrumbs, and the scope a handler runs inside.
 *
 * @internal
 */
export type ReactionDeps<
  TEvents extends Schemas,
  TActions extends Schemas,
  TActor extends Actor = Actor,
> = {
  readonly logger: Logger;
  /**
   * What a handler runs inside — its `IAct` facade and its triggering-event
   * context — assembled by the orchestrator. How either half works is not
   * this module's business; it receives the scope whole and calls it.
   */
  readonly reaction_scope: ReactionScope<TEvents, TActions, TActor>;
};

/**
 * Shared finalization: log the error and decide retry vs. block. The
 * error string is *always* surfaced on the failure path — drain-cycle
 * uses `handled > 0` (not `error` presence) to decide whether to ack
 * the partial progress, so the message can travel for trace + blocked
 * record without affecting the ack/skip choice.
 */
function finalize(
  lease: Lease,
  handled: number,
  at: number,
  error: Error | undefined,
  options: ReactionOptions,
  logger: Logger,
  failed_at?: number
): HandleResult {
  if (!error) return { lease, handled, acked_at: at };
  logger.error(error);
  // A `NonRetryableError` from the handler short-circuits the retry
  // budget — block on first attempt when the operator has opted in via
  // `blockOnError`. When `blockOnError` is false, the operator has
  // explicitly chosen "retry forever," so we don't override that.
  const non_retryable = error instanceof NonRetryableError;
  const block =
    options.blockOnError &&
    (non_retryable || lease.retry >= options.maxRetries);
  if (block)
    logger.error(
      non_retryable
        ? `Blocking ${lease.stream} on non-retryable error.`
        : `Blocking ${lease.stream} after ${lease.retry} retries.`
    );
  // Backoff applies only on retry paths — successful handles and terminal
  // blocks never defer. `lease.retry` here is the just-failed attempt's
  // counter, so the delay paces the *next* attempt.
  const next_attempt_at =
    !block && options.backoff
      ? Date.now() + compute_backoff_delay(lease.retry, options.backoff)
      : undefined;
  return {
    lease,
    handled,
    acked_at: at,
    error: error.message,
    block,
    next_attempt_at,
    failed_at,
  };
}

/**
 * Builds the per-event reaction dispatcher passed to `run_drain_cycle`.
 *
 * The triggering event is installed as ambient context around each handler
 * call, so `action()` resolves it as `reactingTo` (#587, #1541). Ambient
 * rather than bound to the `IAct` argument, so a handler that dispatches
 * through a captured `app` inherits the chain too.
 *
 * @internal
 */
export function build_handle<
  TEvents extends Schemas,
  TActions extends Schemas,
  TActor extends Actor = Actor,
>(deps: ReactionDeps<TEvents, TActions, TActor>): Handle<TEvents> {
  const { logger, reaction_scope } = deps;
  return async (lease, payloads) => {
    if (payloads.length === 0) return { lease, handled: 0, acked_at: lease.at };

    const stream = lease.stream;
    // One event fans out to one payload per matching reaction, and the
    // watermark may only advance past an event once EVERY reaction on it
    // has handled it — acking mid-group would silently drop the remaining
    // reactions on redelivery (#1179). Track the last payload index per
    // event id (payloads arrive in event order) so `at` advances exactly
    // when a group completes; `handled` counts completed events, not
    // payloads, so a mid-group failure with no completed events is a
    // no-progress result (no ack, retry counter advances toward
    // `blockOnError` instead of resetting on every partial pass).
    const last_index_of = new Map<number, number>();
    for (let i = 0; i < payloads.length; i++)
      last_index_of.set(payloads[i].event.id, i);
    let at = lease.at;
    let handled = 0;

    if (lease.retry > 0)
      logger.warn(
        `Retrying ${stream}@${payloads.at(0)!.event.id} (${lease.retry}).`
      );

    for (let i = 0; i < payloads.length; i++) {
      const payload = payloads[i];
      const { event, handler } = payload;
      try {
        // Scoped per payload, not hoisted to the lease: the context has to
        // unwind with the handler so it never reaches the drain cycle, and
        // work a handler started without awaiting has to resume into its
        // own event's frame.
        await reaction_scope.run(event as Committed<Schemas, string>, () =>
          handler(event, stream, reaction_scope.app)
        );
        if (last_index_of.get(event.id) === i) {
          at = event.id;
          handled++;
        }
      } catch (error) {
        // A defer is not a failure: hold the triggering events pending
        // (exclude from ack via `defer`), don't bump `retry`, and re-visit
        // the stream at the carried due-time (#1090). `acked_at` is unused on
        // the defer path — drain never acks a deferred result.
        if (error instanceof DeferSignal)
          return {
            lease,
            handled,
            acked_at: at,
            defer: resolve_defer_at<TEvents>(error.when, event),
          };
        // A close request advances past the triggering event (so the
        // requesting reaction isn't counted as in-flight by the close-cycle
        // guard) and hands the target to the orchestrator's on_close (#1090).
        if (error instanceof CloseSignal)
          return {
            lease,
            handled: handled + 1,
            // Advance to the live head the handler evaluated against (when
            // provided) so the close-cycle guard sees this reaction caught up.
            acked_at: error.at ?? event.id,
            // Close the signalled stream (the autoclose reaction's aggregate,
            // which differs from its synthetic lease stream); a self-closing
            // user reaction omits it and closes its own lease stream. A
            // carried `before` makes it a windowed close (prune, not retire).
            close: {
              stream: error.stream ?? stream,
              archive: error.archive,
              before: error.before,
            },
          };
        return finalize(
          lease,
          handled,
          at,
          error as Error,
          payload.options,
          logger,
          event.id
        );
      }
    }
    return finalize(lease, handled, at, undefined, payloads[0].options, logger);
  };
}

/**
 * Builds the bulk reaction dispatcher passed to `run_drain_cycle`. All events
 * for a static-target projection are handed to a single callback so the
 * projection can do one transaction per drain (catch-up replays especially).
 *
 * @internal
 */
export function build_handle_batch<TEvents extends Schemas>(
  logger: Logger
): HandleBatch<TEvents> {
  return async (
    lease: Lease,
    payloads: ReactionPayload<TEvents>[],
    batchHandler: BatchHandler<TEvents>
  ) => {
    const stream = lease.stream;
    const events = payloads.map(
      (p) => p.event as Committed<TEvents, keyof TEvents & string>
    );
    const options = payloads[0].options;

    if (lease.retry > 0)
      logger.warn(`Retrying batch ${stream}@${events[0].id} (${lease.retry}).`);

    try {
      await batchHandler(events, stream);
      return finalize(
        lease,
        events.length,
        events.at(-1)!.id,
        undefined,
        options,
        logger
      );
    } catch (error) {
      return finalize(lease, 0, lease.at, error as Error, options, logger);
    }
  };
}
